import { useState } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router'
import { useCreateDialog, useEditRoute } from '@/contexts/CreateDialogContext'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from '@tanstack/react-form'
import { useTranslation } from 'react-i18next'
import { CreateObjectButton } from '@/components/CreateObjectButton'

import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { NumberInput } from '@/components/ui/number-input'
import { Building, Users, Percent, MapPin, Flag, Ban, SlidersHorizontal, Compass, Plus, Check, Maximize, X as XIcon, ChartLine, Settings, ArrowUpRight } from 'lucide-react'
import { ChipMultiSelect } from '../../components/ChipMultiSelect'
import { LabelAutocomplete } from '../../components/LabelAutocomplete'
import {
  StatsBar,
  StatsBarId,
  StatsBarActivity,
} from '../../components/StatsBar'
import { AuthService } from '@/services/core/auth'
import {
  EarningsAccountsApi,
  EarningsAccountInput,
  EarningsAccount,
} from '@/services/earnings/earningsAccountsApi'
import { SitesApi, Site } from '@/services/sites/sitesApi'
import { ApiError, apiErrorDescription } from '@/services/core/apiError'
import { InlineEditTitle } from '../../components/InlineEditTitle'
import { AppShell } from '../../components/AppShell'
import { SearchInput } from '../../components/SearchInput'
import { filterBySearch } from '../../utils/search'
import { FormSkeleton } from '../../components/FormSkeleton'
import { useUnsavedChanges } from '../../hooks/useUnsavedChanges'
import { UnsavedChangesDialog } from '../../components/UnsavedChangesDialog'
import { NetworkObjectLookup } from '../../components/NetworkObjectLookup'
import { SiteTable } from '../../components/SiteTable'
import { BulkActionBar } from '../../components/BulkActionBar'
import { StatsPanel } from '../../components/StatsPanel'
import { RouteTabs } from '../../components/RouteTabs'
import { BlocklistsApi } from '@/services/rules/blocklistsApi'
import { MultiSelect } from '@/components/ui/multi-select'
import { TeamsApi } from '@/services/users/teamsApi'
import { RulesetsApi } from '@/services/rules/rulesetsApi'
import { useNetwork } from '../../contexts/NetworkContext'
import { useCurrentUser } from '../../contexts/UserContext'
import { isSuperAdmin } from '../../data/superAdmins'
import { Card, CardHeader, CardIcon, CardTitle, CardContent } from '@/components/ui/card'
import { ObjectTypeLabel } from '../../components/ObjectTypeLabel'
import { formatDate } from '../../utils/format'
import { ValidationWarnings } from '@/components/ValidationWarnings'
import { ObjectStateToggle, useSaveObjectState, type ObjectState } from '../../components/ObjectStateToggle'
import { VersionHistoryButton } from '@/components/VersionHistoryButton'

// ── Inner form component ────────────────────────────────────────────────────

interface EarningsAccountEditFormProps {
  account: EarningsAccount | null
  id: string
  isNew: boolean
}

function EarningsAccountEditForm({ account, id, isNew }: EarningsAccountEditFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const createDialog = useCreateDialog()
  // A new object's cancel closes the create dialog, or leaves its page.
  const cancelNew = (path: string) => (createDialog ? createDialog.close() : navigate(path))
  const queryClient = useQueryClient()

  const [showAddress, setShowAddress] = useState(() => {
    if (!account) return false
    const a = account.address
    return !!(a.addressLine1 || a.addressLine2 || a.city || a.state || a.postCode || a.country)
  })
  const [siteSelection, setSiteSelection] = useState<Site[]>([])
  const [siteSearch, setSiteSearch] = useState('')
  const [siteBulkLoading, setSiteBulkLoading] = useState(false)

  const siteBulkUpdate = async (label: string, payload: Record<string, unknown>) => {
    setSiteBulkLoading(true)
    try {
      await Promise.all(
        siteSelection.map((s) => SitesApi.update(s.id, { name: s.name, ...payload }))
      )
      queryClient.invalidateQueries({ queryKey: ['sites'] })
      toast.success(t('common.done'), {
        description: t('publishers.doneDetail', { count: siteSelection.length, label }),
        duration: 3000,
      })
      setSiteSelection([])
    } catch (e) {
      toast.error(t('common.error'), {
        description: apiErrorDescription(e),
        duration: 5000,
      })
    } finally {
      setSiteBulkLoading(false)
    }
  }

  const currentUser = useCurrentUser()
  const hasToken = AuthService.isAuthenticated()
  const { data: teams = [] } = useQuery({
    queryKey: ['teams'],
    queryFn: () => TeamsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const { data: rulesets = [] } = useQuery({
    queryKey: ['rulesets'],
    queryFn: () => RulesetsApi.getAll(),
    enabled: hasToken,
    staleTime: 5 * 60 * 1000,
  })
  const showAdmin = isSuperAdmin(currentUser?.username)
  const { currentNetwork } = useNetwork()
  const isMarketplace = currentNetwork?.marketplacePlatform ?? false

  const { data: blocklists = [] } = useQuery({
    queryKey: ['blocklists'],
    queryFn: () => BlocklistsApi.getAll(),
    enabled: isMarketplace,
    staleTime: Infinity,
  })

  const form = useForm({
    defaultValues: {
      name: account?.name ?? '',
      description: account?.description ?? '',
      ownerTeamId: account?.ownerTeamId ?? '',
      externalReference: account?.externalReference ?? '',
      revenueShare: account?.revenueShare ?? 0,
      directDealRevenueShare: account?.directDealRevenueShare ?? 0,
      labels: account?.labels ?? [] as string[],
      blocklist: account?.blocklist ?? '' as string,
      filterTeamsType: account?.filterTeamsType || 'EXCLUDES' as string,
      filterTeams: account?.filterTeams ?? [] as string[],
      politicalAdvertising: account?.politicalAdvertising ?? '' as string,
      fullPageSizes: account?.fullPageSizes ?? {} as Record<string, string>,
      siteRulesetId: account?.siteRuleset?.id ?? '' as string,
      targetingRulesetIds: account?.targetingRulesets.map((r) => r.id) ?? [] as string[],
      sizeRulesetIds: account?.sizeRulesets.map((r) => r.id) ?? [] as string[],
      layoutRulesetIds: account?.layoutRulesets.map((r) => r.id) ?? [] as string[],
      addressLine1: account?.address.addressLine1 ?? '',
      addressLine2: account?.address.addressLine2 ?? '',
      city: account?.address.city ?? '',
      addrState: account?.address.state ?? '',
      postCode: account?.address.postCode ?? '',
      addrCountry: account?.address.country ?? '',
    },
    onSubmit: ({ value }) => {
      mutation.mutate({
        name: value.name,
        description: value.description || undefined,
        ownerTeamId: value.ownerTeamId || undefined,
        externalReference: value.externalReference || undefined,
        revenueShare: value.revenueShare || undefined,
        directDealRevenueShare: value.directDealRevenueShare || undefined,
        labels: value.labels,
        blocklist: value.blocklist ? { id: value.blocklist } : null,
        objectState: (account?.objectState ?? 'ACTIVE') as ObjectState,
        ...(showAddress && {
          address: {
            addressLine1: value.addressLine1 || undefined,
            addressLine2: value.addressLine2 || undefined,
            city: value.city || undefined,
            state: value.addrState || undefined,
            postCode: value.postCode || undefined,
            country: value.addrCountry || undefined,
          },
        }),
        ...(isMarketplace && {
          politicalAdvertising: value.politicalAdvertising || undefined,
          filterTeamsType: value.filterTeamsType,
          filterTeams:
            value.filterTeamsType !== 'NONE' ? value.filterTeams.map((id) => ({ id })) : [],
          siteRuleset: value.siteRulesetId ? { id: value.siteRulesetId } : null,
          targetingRulesets: value.targetingRulesetIds.map((id) => ({ id })),
          sizeRulesets: value.sizeRulesetIds.map((id) => ({ id })),
          layoutRulesets: value.layoutRulesetIds.map((id) => ({ id })),
        }),
        ...(showAdmin && { fullPageSizes: value.fullPageSizes }),
      })
    },
  })
  const saveObjectState = useSaveObjectState(
    (objectState) => EarningsAccountsApi.update(id!, { name: account?.name ?? '', objectState }),
    ['earningsAccount', id],
    ['earningsAccounts']
  )
  const { isDirty, markClean, blocker } = useUnsavedChanges(form)

  const { data: publisherSites = [], isLoading: sitesLoading } = useQuery({
    queryKey: ['sites', 'earningsAccount', id],
    queryFn: () => SitesApi.getByEarningsAccount(id),
    enabled: hasToken && !isNew,
    staleTime: 5 * 60 * 1000,
  })

  const mutation = useMutation({
    mutationFn: (input: EarningsAccountInput) =>
      isNew ? EarningsAccountsApi.create(id!, input) : EarningsAccountsApi.update(id!, input),
    onSuccess: (data) => {
      queryClient.setQueryData(['earningsAccount', id], data)
      queryClient.setQueryData(['earningsAccounts'], (old: EarningsAccount[] | undefined) => {
        if (!old) return [data]
        const idx = old.findIndex((a) => a.id === data.id)
        return idx >= 0 ? old.map((a) => (a.id === data.id ? data : a)) : [...old, data]
      })
      markClean()
      if (isNew) {
        navigate(`/publishing/earnings/${id}`, { replace: !createDialog })
      }
      toast.success(t('common.saved'), {
        description: isNew ? t('publishers.createdSuccess') : t('publishers.savedSuccess'),
        duration: 3000,
      })
    },
    onError: (error) => {
      const summary =
        error instanceof ApiError ? error.message : isNew ? t('publishers.createFailed') : t('publishers.saveFailed')
      const detail = apiErrorDescription(error)
      toast.error(summary, {
        description: detail,
        duration: 5000,
      })
    },
  })

  const crumbs = [
    { label: t('publishers.title'), href: '/publishing/earnings' },
    { label: isNew ? t('publishers.newTitle') : (account?.name ?? t('publishers.editTitle')) },
  ]

  return (
    <AppShell crumbs={crumbs} showBack>
      <form
        className="ad-page"
        onSubmit={(e) => {
          e.preventDefault()
          form.handleSubmit()
        }}
      >
        <ValidationWarnings warnings={account?.validationWarnings} />
        <div className="ad-page__header">
          <div className="ad-page__title-block">
            <ObjectTypeLabel type={t('objectTypes.earningsAccount')} />
            <h1>
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                  onSubmit: ({ value }) => (value.trim() ? undefined : t('common.nameRequired')),
                }}
              >
                {(field) => (
                  <InlineEditTitle
                    value={field.state.value}
                    onChange={(v) => field.handleChange(v)}
                    placeholder={t('publishers.namePlaceholder')}
                    required
                    error={field.state.meta.errors[0] as string | undefined}
                  />
                )}
              </form.Field>
            </h1>
          </div>
          {account && !isNew && (
            <div className="ad-page__actions">
              <VersionHistoryButton />
              <ObjectStateToggle value={account!.objectState} onChange={saveObjectState} />
            </div>
          )}
        </div>

        {account && !isNew && (
          <StatsBar
            items={[
              {
                label: t('common.id'),
                content: <StatsBarId value={account.id} />,
              },
              {
                label: t('stats.activity'),
                content: (
                  <StatsBarActivity
                    created={formatDate(account.createTime)}
                    updated={formatDate(account.updateTime)}
                  />
                ),
              },
            ]}
          />
        )}

        <div className="ad-edit-columns">
          {/* Left column: details + address + info */}
          <div className="ad-edit-columns__info">
            <Card>
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Building} />
                  {t('publishers.details')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('common.description')}</label>
                    <form.Field name="description">
                      {(field) => (
                        <Textarea
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('publishers.descriptionPlaceholder')}
                          rows={2}
                          style={{ resize: 'vertical' }}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('publishers.labels')}</label>
                    <form.Field name="labels">
                      {(field) => (
                        <LabelAutocomplete
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v)}
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field full">
                    <label>{t('publishers.externalReference')}</label>
                    <form.Field name="externalReference">
                      {(field) => (
                        <Input
                          value={field.state.value}
                          onChange={(e) => field.handleChange(e.target.value)}
                          placeholder={t('publishers.externalReferencePlaceholder')}
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Owner team */}
            <Card tone="people">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Users} />
                  {t('common.team')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field full">
                    <label>{t('publishers.ownerTeam')}</label>
                    <form.Field name="ownerTeamId">
                      {(field) => (
                        <Select label={t('publishers.ownerTeam')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                          <SelectTrigger><SelectValue placeholder={t('publishers.selectTeam')}>{teams.find((o) => o.id === field.state.value)?.name ?? null}</SelectValue></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__clear__">&mdash;</SelectItem>
                            {teams.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Revenue share */}
            <Card tone="money">
              <CardHeader>
                <CardTitle>
                  <CardIcon icon={Percent} />
                  {t('publishers.revenueShare')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="field-grid">
                  <div className="p-field">
                    <label>{t('publishers.revenueSharePercent')}</label>
                    <form.Field name="revenueShare">
                      {(field) => (
                        <NumberInput
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v ?? 0)}
                          min={0}
                          max={100}
                          placeholder="0"
                        />
                      )}
                    </form.Field>
                  </div>
                  <div className="p-field">
                    <label>{t('publishers.directDealRevenueShare')}</label>
                    <form.Field name="directDealRevenueShare">
                      {(field) => (
                        <NumberInput
                          value={field.state.value}
                          onChange={(v) => field.handleChange(v ?? 0)}
                          min={0}
                          max={100}
                          placeholder="0"
                        />
                      )}
                    </form.Field>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Address */}
            {!showAddress ? (
              <button
                type="button"
                onClick={() => setShowAddress(true)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--ad-primary)',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: 0,
                }}
              >
                <MapPin className="size-3" />
                {t('publishers.addAddress')}
              </button>
            ) : (
              <Card tone="people">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={MapPin} />
                    {t('address.title')}
                  </CardTitle>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddress(false)
                      form.setFieldValue('addressLine1', '')
                      form.setFieldValue('addressLine2', '')
                      form.setFieldValue('city', '')
                      form.setFieldValue('addrState', '')
                      form.setFieldValue('postCode', '')
                      form.setFieldValue('addrCountry', '')
                    }}
                  >
                    <XIcon className="size-4" />
                  </Button>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('address.line1')}</label>
                      <form.Field name="addressLine1">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('address.streetPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <label>{t('address.line2')}</label>
                      <form.Field name="addressLine2">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('address.aptPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <label>{t('address.city')}</label>
                      <form.Field name="city">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('address.cityPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <label>{t('address.stateRegion')}</label>
                      <form.Field name="addrState">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('address.statePlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <label>{t('address.postCode')}</label>
                      <form.Field name="postCode">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('address.postCodePlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field">
                      <label>{t('address.country')}</label>
                      <form.Field name="addrCountry">
                        {(field) => (
                          <Input
                            value={field.state.value}
                            onChange={(e) => field.handleChange(e.target.value)}
                            placeholder={t('address.countryPlaceholder')}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Political ads settings — marketplace only */}
            {isMarketplace && (
              <Card tone="attention">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Flag} />
                    {t('publishers.politicalAds')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <form.Field name="politicalAdvertising">
                    {(field) => (
                      <>
                        <div className="flex gap-0.5 rounded-md border border-(--ad-border) overflow-hidden">
                          {[
                            { label: t('publishers.policyBlocked'), value: 'BLOCKED' },
                            { label: t('publishers.policyRequiresApproval'), value: 'REQUIRES_APPROVAL' },
                            { label: t('publishers.policyAllowed'), value: 'ALLOWED' },
                          ].map((o) => (
                            <button key={o.value} type="button"
                              className={`px-3 py-1.5 text-sm ${field.state.value === o.value ? 'bg-(--ad-primary) text-white' : 'bg-(--ad-surface) text-(--ad-text) hover:bg-(--ad-neutral-soft)'}`}
                              onClick={() => field.handleChange(o.value)}>
                              {o.label}
                            </button>
                          ))}
                        </div>
                        <p style={{ fontSize: 12, color: 'var(--ad-text-muted)', marginTop: 10 }}>
                          {field.state.value === 'ALLOWED' && t('publishers.policyAllowedDesc')}
                          {field.state.value === 'REQUIRES_APPROVAL' && t('publishers.policyApprovalDesc')}
                          {(!field.state.value || field.state.value === 'BLOCKED') && t('publishers.policyBlockedDesc')}
                        </p>
                      </>
                    )}
                  </form.Field>
                </CardContent>
              </Card>
            )}

            {/* Team filtering — marketplace only */}
            {isMarketplace && (
              <Card tone="people">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Users} />
                    {t('publishers.teamFiltering')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('publishers.filterType')}</label>
                      <form.Field name="filterTeamsType">
                        {(field) => (
                          <Select label={t('publishers.filterType')} value={field.state.value} onValueChange={(v) => {
                              field.handleChange(v ?? '')
                              if (v === 'NONE') {
                                form.setFieldValue('filterTeams', [])
                              }
                            }}>
                            <SelectTrigger><SelectValue>{[
                                { label: t('publishers.filterNone'), value: 'NONE' },
                                { label: t('publishers.filterOnlyAllow'), value: 'INCLUDES' },
                                { label: t('publishers.filterBlock'), value: 'EXCLUDES' },
                              ].find((o) => o.value === field.state.value)?.label ?? null}</SelectValue></SelectTrigger>
                            <SelectContent>
                              {[
                                { label: t('publishers.filterNone'), value: 'NONE' },
                                { label: t('publishers.filterOnlyAllow'), value: 'INCLUDES' },
                                { label: t('publishers.filterBlock'), value: 'EXCLUDES' },
                              ].map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        )}
                      </form.Field>
                    </div>
                    <form.Field name="filterTeamsType">
                      {(typeField) =>
                        typeField.state.value !== 'NONE' && (
                          <div className="p-field full">
                            <label>
                              {typeField.state.value === 'INCLUDES'
                                ? t('publishers.teamsToInclude')
                                : t('publishers.teamsToExclude')}
                            </label>
                            <form.Field name="filterTeams">
                              {(field) => (
                                <ChipMultiSelect
                                  value={field.state.value}
                                  onChange={(e) => field.handleChange((e.value as string[]) ?? [])}
                                  options={teams}
                                  optionLabel="name"
                                  optionValue="id"
                                  placeholder={t('publishers.selectTeams')}
                                />
                              )}
                            </form.Field>
                          </div>
                        )
                      }
                    </form.Field>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Blocklist — marketplace only */}
            {isMarketplace && (
              <Card tone="attention">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={Ban} />
                    {t('publishers.blocklist')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('publishers.blocklists')}</label>
                      <form.Field name="blocklist">
                        {(field) => (
                          <ChipMultiSelect
                            single
                            value={field.state.value}
                            onChange={(e) => field.handleChange((e.value as string) ?? '')}
                            options={blocklists}
                            optionLabel="name"
                            optionValue="id"
                            placeholder={t('publishers.selectBlocklist')}
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Rulesets — marketplace only */}
            {isMarketplace && (
              <Card tone="delivery">
                <CardHeader>
                  <CardTitle>
                    <CardIcon icon={SlidersHorizontal} />
                    {t('publishers.rulesets')}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="field-grid">
                    <div className="p-field full">
                      <label>{t('publishers.inventoryRuleset')}</label>
                      <form.Field name="siteRulesetId">
                        {(field) => (
                          <div className="flex gap-2">
                            <Select label={t('publishers.inventoryRuleset')} value={field.state.value} onValueChange={(v) => field.handleChange(v === '__clear__' ? '' : (v ?? ''))}>
                              <SelectTrigger><SelectValue placeholder={t('publishers.selectInventoryRuleset')}>{rulesets.find((r) => r.id === field.state.value)?.name ?? null}</SelectValue></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__clear__">&mdash;</SelectItem>
                                {rulesets.filter((r) => r.type === 'INVENTORY' && r.objectState === 'ACTIVE').map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            {field.state.value && (
                              <Button asChild variant="outline" size="icon" className="shrink-0" title={t('rulesets.openRuleset')}>
                                <RouterLink to={`/publishing/rulesets/${field.state.value}`} aria-label={t('rulesets.openRuleset')}>
                                  <ArrowUpRight className="size-4" />
                                </RouterLink>
                              </Button>
                            )}
                          </div>
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <label>{t('publishers.targetingRulesets')}</label>
                      <form.Field name="targetingRulesetIds">
                        {(field) => (
                          <MultiSelect
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            options={rulesets.filter((r) => r.type === 'TARGETING' && r.objectState === 'ACTIVE').map((r) => ({ label: r.name, value: r.id }))}
                            href={(rulesetId) => `/publishing/rulesets/${rulesetId}`}
                            placeholder={t('publishers.selectTargetingRulesets')}
                            display="chip"
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <label>{t('publishers.sizeRulesets')}</label>
                      <form.Field name="sizeRulesetIds">
                        {(field) => (
                          <MultiSelect
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            options={rulesets.filter((r) => r.type === 'SIZE' && r.objectState === 'ACTIVE').map((r) => ({ label: r.name, value: r.id }))}
                            href={(rulesetId) => `/publishing/rulesets/${rulesetId}`}
                            placeholder={t('publishers.selectSizeRulesets')}
                            display="chip"
                          />
                        )}
                      </form.Field>
                    </div>
                    <div className="p-field full">
                      <label>{t('publishers.layoutRulesets')}</label>
                      <form.Field name="layoutRulesetIds">
                        {(field) => (
                          <MultiSelect
                            value={field.state.value}
                            onChange={(v) => field.handleChange(v)}
                            options={rulesets.filter((r) => r.type === 'LAYOUT' && r.objectState === 'ACTIVE').map((r) => ({ label: r.name, value: r.id }))}
                            href={(rulesetId) => `/publishing/rulesets/${rulesetId}`}
                            placeholder={t('publishers.selectLayoutRulesets')}
                            display="chip"
                          />
                        )}
                      </form.Field>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Sites belonging to this publisher */}
            {!isNew && (
              <Card tone="delivery" className="overflow-hidden">
                <CardHeader className="flex flex-row flex-wrap items-center gap-3">
                  <CardTitle>
                    <CardIcon icon={Compass} />
                    Sites
                    {publisherSites.length > 0 && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: 'var(--ad-text-muted)',
                          background: 'var(--ad-neutral-soft)',
                          padding: '1px 8px',
                          borderRadius: 999,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                        }}
                      >
                        {publisherSites.length}
                      </span>
                    )}
                  </CardTitle>
                  <SearchInput value={siteSearch} onChange={setSiteSearch} className="ml-auto" />
                  <CreateObjectButton type="site" variant="outline" context={{ earningsAccount: id }} />
                </CardHeader>
                <BulkActionBar
                  count={siteSelection.length}
                  actions={[
                    {
                      label: t('common.activate'),
                      icon: 'pi pi-check-circle',
                      severity: 'success',
                      onClick: () => siteBulkUpdate(t('common.activate').toLowerCase(), { objectState: 'ACTIVE' }),
                    },
                    {
                      label: t('common.deactivate'),
                      icon: 'pi pi-ban',
                      severity: 'danger',
                      onClick: () => siteBulkUpdate(t('common.deactivate').toLowerCase(), { objectState: 'INACTIVE' }),
                    },
                  ]}
                  onClear={() => setSiteSelection([])}
                  loading={siteBulkLoading}
                />
                <CardContent className="p-0">
                  <SiteTable
                    sites={filterBySearch(publisherSites, siteSearch)}
                    loading={sitesLoading}
                    selection={siteSelection}
                    onSelectionChange={setSiteSelection}
                    emptyMessage={t('publishers.noSitesYet')}
                  />
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right column: tabbed panel */}
          {!isNew && id && (
            <div className="ad-panel">
              <RouteTabs
                tabs={[
                  {
                    key: 'statistics',
                    label: t('publishers.statistics'),
                    icon: <ChartLine />,
                    content: () => (
                      <StatsPanel
                        query={{ earningsAccountId: id, groupBy: 'HALF_HOURLY' }}
                        storageKey="publisher"
                      />
                    ),
                  },
                  ...(showAdmin
                    ? [
                        {
                          key: 'admin',
                          label: t('publishers.adminTab'),
                          icon: <Settings />,
                          content: () => (
                            <Card tone="creative">
                              <CardHeader>
                                <CardTitle>
                                  <CardIcon icon={Maximize} />
                                  {t('publishers.fullPageSizes')}
                                </CardTitle>
                              </CardHeader>
                              <CardContent>
                                <p style={{ fontSize: 12, color: 'var(--ad-text-muted)', marginBottom: 12 }}>
                                  {t('publishers.fullPageSizesDesc')}
                                </p>
                                <form.Field name="fullPageSizes">
                                  {(field) => {
                                    const sizes = field.state.value
                                    const keys = Object.keys(sizes)
                                    const updateSize = (key: string, width: number, height: number) => {
                                      field.handleChange({ ...sizes, [key]: `${width}x${height}` })
                                    }
                                    const parseSize = (val: string): [number, number] => {
                                      const parts = val.split('x')
                                      return [parseInt(parts[0]) || 0, parseInt(parts[1]) || 0]
                                    }
                                    const removeSize = (key: string) => {
                                      const next = { ...sizes }
                                      delete next[key]
                                      field.handleChange(next)
                                    }
                                    const addSize = () => {
                                      field.handleChange({ ...sizes, '': '' })
                                    }
                                    return (
                                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                        {keys.map((key, i) => (
                                          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                            <Input
                                              value={key}
                                              onChange={(e) => {
                                                const newKey = e.target.value
                                                const entries = Object.entries(sizes)
                                                const next: Record<string, string> = {}
                                                for (const [k, v] of entries) {
                                                  next[k === key ? newKey : k] = v
                                                }
                                                field.handleChange(next)
                                              }}
                                              placeholder={t('publishers.placementKeyPlaceholder')}
                                              style={{ flex: 1 }}
                                            />
                                            {(() => {
                                              const [w, h] = parseSize(sizes[key])
                                              return (
                                                <>
                                                  <NumberInput
                                                    value={w || null}
                                                    onChange={(v) => updateSize(key, v ?? 0, h)}
                                                    placeholder="W"
                                                    style={{ width: 60 }}
                                                    min={0}
                                                  />
                                                  <span style={{ color: 'var(--ad-text-muted)' }}>x</span>
                                                  <NumberInput
                                                    value={h || null}
                                                    onChange={(v) => updateSize(key, w, v ?? 0)}
                                                    placeholder="H"
                                                    style={{ width: 60 }}
                                                    min={0}
                                                  />
                                                </>
                                              )
                                            })()}
                                            <Button
                                              type="button"
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => removeSize(key)}
                                            >
                                              <XIcon className="size-3.5" />
                                            </Button>
                                          </div>
                                        ))}
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="sm"
                                          onClick={addSize}
                                          style={{ alignSelf: 'flex-start' }}
                                        >
                                          <Plus className="size-3.5" />
                                          {t('publishers.addPlacement')}
                                        </Button>
                                      </div>
                                    )
                                  }}
                                </form.Field>
                              </CardContent>
                            </Card>
                          ),
                        },
                      ]
                    : []),
                ]}
              />
            </div>
          )}
        </div>

        {/* Save bar */}
        <div
          className={`detail-savebar${isDirty || isNew || mutation.isPending ? ' is-visible' : ''}`}
        >
          <div className="detail-savebar__left">
            {mutation.isPending && (
              <>
                <i className="pi pi-spin pi-spinner" />
                {isNew ? t('common.creating') : t('common.saving')}
              </>
            )}
          </div>
          <div className="detail-savebar__actions">
            <Button
              type="button"
              variant="ghost"
              onClick={() => cancelNew('/publishing/earnings')}
              disabled={mutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={mutation.isPending}
            >
              {isNew ? <Plus className="size-4" /> : <Check className="size-4" />}
              {isNew ? t('publishers.createPublisher') : t('common.save')}
            </Button>
          </div>
        </div>
      </form>
      <UnsavedChangesDialog blocker={blocker} />
    </AppShell>
  )
}

// ── Outer page ──────────────────────────────────────────────────────────────

export function EarningsAccountEditPage() {
  const { t } = useTranslation()
  const { id, searchParams } = useEditRoute()
  const isNew = searchParams.get('new') === 'true'
  const hasToken = AuthService.isAuthenticated()

  const { data: account, isLoading, isError } = useQuery({
    queryKey: ['earningsAccount', id],
    queryFn: () => EarningsAccountsApi.getById(id!),
    enabled: !!id && !isNew && hasToken,
    retry: false,
  })

  const crumbsLoading = [
    { label: t('publishers.title'), href: '/publishing/earnings' },
    { label: isNew ? t('publishers.newTitle') : t('publishers.editTitle') },
  ]

  if (isError && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <NetworkObjectLookup objectId={id!} />
      </AppShell>
    )
  }

  if (isLoading && !isNew) {
    return (
      <AppShell crumbs={crumbsLoading} showBack>
        <FormSkeleton rows={3} columns={2} />
      </AppShell>
    )
  }

  return (
    <EarningsAccountEditForm
      key={id}
      account={account ?? null}
      id={id!}
      isNew={isNew}
    />
  )
}
